import React, { useContext } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";

import MUIGrid from "@mui/material/Grid";
import MUITypography from "@mui/material/Typography";
import MUIQuizIcon from "@mui/icons-material/Quiz";
import MUIButton from "@mui/material/Button";
import Card from "../../Card";
import InfoItem from "../../InfoItem";
import moment from "moment";
import EmptyState from "../../EmptyState";

/**
 * CardChartIntakeQuestionnaire renders a Card component
 * displaying order intake questionnaire information.
 * @returns {JSX.Element}
 */

const title = "Intake Questionnaire";
const icon = MUIQuizIcon;
const className = "CardChartIntakeQuestionnaire";

export default function CardChartIntakeQuestionnaire(props) {
    const chartContext = useContext(ChartContext);
    const order = chartContext.chart.orderActivity[chartContext?.orderId];
    const intakeQuestionnaire = chartContext.chart.intakeQuestionnaire?.[order?.currentIntakeQuestionnaireId ?? null];
    const intakeQuestionnaireJsonResponse = intakeQuestionnaire ? JSON.parse(intakeQuestionnaire.jsonResponse) : {};
    const homeHealthComments = intakeQuestionnaireJsonResponse?.homeHealthComments || "".replace(/(\r\n|\n|\r)/g, "<br>");
    const planToChangeInsuranceComments = intakeQuestionnaireJsonResponse?.planToChangeInsuranceComments || "".replace(/(\r\n|\n|\r)/g, "<br>");
    const patientCommitToProcessComments = intakeQuestionnaireJsonResponse?.patientCommitToProcessComments || "".replace(/(\r\n|\n|\r)/g, "<br>");
    const reasonForEquipmentComments = intakeQuestionnaireJsonResponse?.reasonForEquipment || "".replace(/(\r\n|\n|\r)/g, "<br>");
    const COMMENT_PREVIEW_LENGTH = 100;
    const allowedExtraCharacters = 20;
    const [comments, setComments] = React.useState({
        "homeHealthComments":
        {
            message: homeHealthComments,
            sliceLength: homeHealthComments.length < COMMENT_PREVIEW_LENGTH + allowedExtraCharacters ? COMMENT_PREVIEW_LENGTH + allowedExtraCharacters : COMMENT_PREVIEW_LENGTH,
            long: homeHealthComments.length > COMMENT_PREVIEW_LENGTH + allowedExtraCharacters,
            expanded: false
        },
        "planToChangeInsuranceComments": {
            message: planToChangeInsuranceComments,
            sliceLength: planToChangeInsuranceComments.length < COMMENT_PREVIEW_LENGTH + allowedExtraCharacters ? COMMENT_PREVIEW_LENGTH + allowedExtraCharacters : COMMENT_PREVIEW_LENGTH,
            long: planToChangeInsuranceComments.length > COMMENT_PREVIEW_LENGTH + allowedExtraCharacters,
            expanded: false
        },
        "patientCommitToProcessComments": {
            message: patientCommitToProcessComments,
            sliceLength: patientCommitToProcessComments.length < COMMENT_PREVIEW_LENGTH + allowedExtraCharacters ? COMMENT_PREVIEW_LENGTH + allowedExtraCharacters : COMMENT_PREVIEW_LENGTH,
            long: patientCommitToProcessComments.length > COMMENT_PREVIEW_LENGTH + allowedExtraCharacters,
            expanded: false
        },
        "reasonForEquipmentComments": {
            message: reasonForEquipmentComments,
            sliceLength: reasonForEquipmentComments.length < COMMENT_PREVIEW_LENGTH + allowedExtraCharacters ? COMMENT_PREVIEW_LENGTH + allowedExtraCharacters : COMMENT_PREVIEW_LENGTH,
            long: reasonForEquipmentComments.length > COMMENT_PREVIEW_LENGTH + allowedExtraCharacters,
            expanded: false
        }
    });

    /**
     * Expands or collapses the comment text for a given comment key.
     * @param {*} commentKey
     */
    const handleToggleComment = (commentKey) => {
        const expanded = !comments[commentKey].expanded;
        let sliceLength;
        if (expanded) {
            sliceLength = comments[commentKey].message.length
        } else {
            sliceLength = comments[commentKey].message.length < comments[commentKey].sliceLength + allowedExtraCharacters ? COMMENT_PREVIEW_LENGTH + allowedExtraCharacters : COMMENT_PREVIEW_LENGTH;
        }
        setComments((prevComments) => ({
            ...prevComments,
            [commentKey]: {
                message: prevComments[commentKey].message,
                sliceLength: sliceLength,
                long: prevComments[commentKey].long,
                expanded: expanded
            }
        }));
    };

    /**
     * Renders the read-only view displaying intakeQuestionnaire information.
     * @returns {JSX.Element} Grid layout with intakeQuestionnaire information display
     */
    const renderReadOnlyView = () => {
        const homeHealthText = comments["homeHealthComments"].message.slice(0, comments["homeHealthComments"].sliceLength) + (comments["homeHealthComments"].long && !comments["homeHealthComments"].expanded ? "..." : "");
        const reasonForEquipmentText = comments["reasonForEquipmentComments"].message.slice(0, comments["reasonForEquipmentComments"].sliceLength) + (comments["reasonForEquipmentComments"].long && !comments["reasonForEquipmentComments"].expanded ? "..." : "");
        const planToChangeInsuranceText = comments["planToChangeInsuranceComments"].message.slice(0, comments["planToChangeInsuranceComments"].sliceLength) + (comments["planToChangeInsuranceComments"].long && !comments["planToChangeInsuranceComments"].expanded ? "..." : "");
        const patientCommitToProcessText = comments["patientCommitToProcessComments"].message.slice(0, comments["patientCommitToProcessComments"].sliceLength) + (comments["patientCommitToProcessComments"].long && !comments["patientCommitToProcessComments"].expanded ? "..." : "");

        return (
            (Object.values(intakeQuestionnaireJsonResponse).length > 0 ?
                <MUIGrid container rowSpacing={2} spacing={2}>
                    <MUIGrid item xs={12} lg={6}>
                        <InfoItem
                            label={"1. Does the patient have Home Health?"}
                            value={intakeQuestionnaireJsonResponse?.homeHealth !== undefined ? (intakeQuestionnaireJsonResponse?.homeHealth ? "Yes" : "No") : ""}
                        />
                        {intakeQuestionnaireJsonResponse?.homeHealthComments && (
                            <MUITypography variant="body2" sx={{ minHeight: '1.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{homeHealthText || ""}</MUITypography>
                        )}
                        {comments["homeHealthComments"].long && (
                            <MUIButton
                                size="small"
                                sx={{ mt: 1, p: 0, minWidth: 'unset' }}
                                onClick={() => {
                                    handleToggleComment("homeHealthComments");
                                }}
                            >
                                {comments["homeHealthComments"].expanded ? 'Show Less' : 'Show More'}
                            </MUIButton>
                        )}
                    </MUIGrid>
                    <MUIGrid item xs={12} lg={6}>
                        <InfoItem
                            label={"2. Does the patient plan on keeping the same insurance during this process?"}
                            value={intakeQuestionnaireJsonResponse?.planToChangeInsurance !== undefined ? (intakeQuestionnaireJsonResponse?.planToChangeInsurance ? "Yes" : "No") : ""}
                        />
                        {intakeQuestionnaireJsonResponse?.planToChangeInsuranceComments && (
                            <MUITypography variant="body2" sx={{ minHeight: '1.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{planToChangeInsuranceText || ""}</MUITypography>
                        )}
                        {comments["planToChangeInsuranceComments"].long && (
                            <MUIButton
                                size="small"
                                sx={{ mt: 1, p: 0, minWidth: 'unset' }}
                                onClick={() => {
                                    handleToggleComment("planToChangeInsuranceComments");
                                }}
                            >
                                {comments["planToChangeInsuranceComments"].expanded ? 'Show Less' : 'Show More'}
                            </MUIButton>
                        )}
                    </MUIGrid>
                    <MUIGrid item xs={12} lg={6}>
                        <InfoItem
                            label={"3. When is the date of the patient's last physician visit?"}
                            value={moment(intakeQuestionnaireJsonResponse?.lastPhysicianVisitDate).format("M/D/YYYY") || ""}
                        />
                    </MUIGrid>
                    <MUIGrid item xs={12} lg={6}>
                        <InfoItem
                            label={"4. Does the patient commit to the process and to attending evaluations?"}
                            value={intakeQuestionnaireJsonResponse?.patientCommitToProcess !== undefined ? (intakeQuestionnaireJsonResponse?.patientCommitToProcess ? "Yes" : "No") : ""}
                        />
                        {intakeQuestionnaireJsonResponse?.patientCommitToProcessComments && (
                            <MUITypography variant="body2" sx={{ minHeight: '1.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{patientCommitToProcessText || ""}</MUITypography>
                        )}
                        {comments["patientCommitToProcessComments"].long && (
                            <MUIButton
                                size="small"
                                sx={{ mt: 1, p: 0, minWidth: 'unset' }}
                                onClick={() => {
                                    handleToggleComment("patientCommitToProcessComments");
                                }}
                            >
                                {comments["patientCommitToProcessComments"].expanded ? 'Show Less' : 'Show More'}
                            </MUIButton>
                        )}
                    </MUIGrid>
                    <MUIGrid item xs={12} lg={6}>
                        <MUITypography variant="subtitle2">
                            {"5. What is the reason for the equipment?"}
                        </MUITypography>
                        <MUITypography variant="body2" sx={{ minHeight: '1.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {reasonForEquipmentText || "None"}
                        </MUITypography>
                        {comments["reasonForEquipmentComments"].long && (
                            <MUIButton
                                size="small"
                                sx={{ mt: 1, p: 0, minWidth: 'unset' }}
                                onClick={() => {
                                    handleToggleComment("reasonForEquipmentComments");
                                }}
                            >
                                {comments["reasonForEquipmentComments"].expanded ? 'Show Less' : 'Show More'}
                            </MUIButton>
                        )}
                    </MUIGrid>
                    <MUIGrid item xs={12} lg={6}>
                        <MUITypography variant="subtitle2">
                            {"6. Do you currently have any type of mobility assistive equipment?"}
                        </MUITypography>
                        <MUITypography variant="body2" sx={{ minHeight: '1.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {intakeQuestionnaireJsonResponse?.currentMobilityAssistiveEquipment !== undefined
                                ? (intakeQuestionnaireJsonResponse?.currentMobilityAssistiveEquipment
                                    ? `Yes${intakeQuestionnaireJsonResponse?.currentMobilityAssistiveEquipmentDate ? ` - ${moment(intakeQuestionnaireJsonResponse?.currentMobilityAssistiveEquipmentDate).format("M/YYYY")}` : ""}`
                                    : "No")
                                : ""}
                        </MUITypography>
                    </MUIGrid>
                </MUIGrid>
                : (<EmptyState
                    line1="No Intake Questionnaire Submitted"
                    line2="Please check again later!"
                />)
            )
        )
    }
    /**
 * Determines which view to render based on edit mode state.
 * @returns {JSX.Element} Either edit view or read-only view
 */
    const renderContent = () => {
        return (
            renderReadOnlyView()
        );
    }

    return (
        <Card
            renderContent={renderContent}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartIntakeQuestionnaire.meta = { className: className, title: title, icon: icon };

